import { UnoStoreHooks, unoStore } from "@simulanisindia/ui";
import jwtEncode from "jwt-encode";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import moment from "moment";
import * as Yup from 'yup';
import { constantKeyMap, GROUP_TAB_TYPE, USER_TAB_TYPE } from "../constant/constants";
import { UserSimpleIcon } from "../assets/Icons";
import { BY_PASS_PERMISSION } from "../constant/permission";

export const VALIDATE_REGEX = /[`~!#$%^&*()={}\[\]\\|:";'<>?,./]/;
export const redirectApplication = (userToken, link) => {
  const payload = { token: userToken };
  const token = jwtEncode(payload, "");
  window.open(`${link}?referrer=${token}`);
};

export function debounce(func, wait) {
  let timeout;
  return function (...args) {
    const context = this;
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(context, args), wait);
  };
}

export const downloadExcel = async ({ name, header, tableData }) => {
  const workbook = XLSX.utils.book_new();
  const worksheet = XLSX.utils.aoa_to_sheet([header, ...tableData]);
  XLSX.utils.book_append_sheet(workbook, worksheet, "KPI List");
  XLSX.writeFile(workbook, `${name}.xlsx`);
  return true;
};

export const downloadPdf = async ({ groupName, mode = "portrait", heading, header, tableData }) => {
  const doc = new jsPDF(mode);
  doc.setFontSize(12);
  doc.text(heading, 10, 10);
  doc.text(`Group name: ${groupName}`, 10, 20);
  doc.text(`Report Generated at:, ${moment().format("lll")}`, 10, 30);

  autoTable(doc, {
    head: [header],
    body: tableData,
    startY: 40,
  });
  doc.save("list.pdf");
  return true;
};

export const toggleArrayByObject = (array, newItem) => {
  const items = Array.isArray(newItem) ? newItem : [newItem];
  let result = [...array];
  items.forEach((obj) => {
    const index = result.findIndex((item) => item.id === obj.id);
    if (index !== -1) {
      result = result.filter((item) => item.id !== obj.id);
    } else {
      result.push(obj);
    }
  });
  return result;
};

export const toggleArraySingleValue = (array, id) => {
  if (array.includes(id)) {
    return array.filter((item) => item !== id);
  } else {
    return [...array, id];
  }
};

export const extractAllGroupChildren = (data, result = []) => {
  if (data.hasOwnProperty("id") && data.hasOwnProperty("name")) {
    result.push({ id: data.id, name: data.name, avatar: data.avatar || null });
  }

  if (Array.isArray(data.children)) {
    data.children.forEach((child) => extractAllGroupChildren(child, result));
  }

  return result;
};

export const getPlaceholderName = (name) => {
  if (!name) return "";
  return name
    .split(" ")
    .slice(0, 2)
    .map((e) => e.charAt(0))
    .join("")
    .toUpperCase();
};

export const modalName = {
  GROUP_ADD: "GROUP_ADD",
  USER_ADD: "USER_ADD",
  GROUP_CSV: "GROUP_CSV",
  USER_CSV: "USER_CSV",
  TABLE_CSV: "TABLE_CSV",
  GROUP_USER_MOVE: "GROUP_USER_MOVE",
  SUCCESSFULLY_POPUP: "SUCCESSFULLY_POPUP",
  CONFIRM_BOX: "CONFIRM_BOX",
  SHARE_RESOURCE: "SHARE_RESOURCE",
  PASSWORD_CHANGE: "PASSWORD_CHANGE",
  ASSIGN_ROLE: "ASSIGN_ROLE"
};

export const platformNames = ["web", "standalonevr", "desktopvr", "desktop", "mobile", "ios", "macos"];

export const makeFilterParams = (list = [], checkPlatformKey = false) => {
  const queryParams = {
    tagIds: [],
    platforms: [],
    categoryIds: [],
  };
  list.forEach((filter) => {
    if (checkPlatformKey && filter?.type === "platform") {
      queryParams.platforms.push((filter.name).replace(/\s+/g, "").toLowerCase());
    } else if (filter?.type === "tagCategory") {
      queryParams.categoryIds.push(filter.id)
    } else {
      queryParams.tagIds.push(filter.value);
    }
  });
  return queryParams;
};

export const passwordRequirements = (passwordPolicy) => {
  const requirements = [];

  if (passwordPolicy.upperCase) {
    requirements.push({ key: "upperCase", text: "At least 1 uppercase letter", regex: /[A-Z]/ });
  }
  if (passwordPolicy.number) {
    requirements.push({ key: "number", text: "At least 1 number", regex: /[0-9]/ });
  }
  if (passwordPolicy.specialChar) {
    requirements.push({ key: "specialChar", text: "At least 1 special character", regex: /[!@#$%^&*(),.?":{}|<>]/ });
  }
  if (passwordPolicy.min) {
    requirements.push({ key: "min", text: `At least ${passwordPolicy.min} characters`, regex: new RegExp(`^.{${passwordPolicy.min},}$`) });
  }
  if (passwordPolicy.max !== null && passwordPolicy.max !== undefined) {
    requirements.push({ key: "max", text: `Password cannot contain more than ${passwordPolicy.max} characters`, regex: new RegExp(`^.{1,${passwordPolicy.max}}$`) });
  }

  return requirements;
};


export const passwordStrengthColor = (metConditions, requirements) => {
  if (metConditions >= requirements.length) return 'green';
  if (metConditions >= Math.ceil(requirements.length / 2)) return 'orange';
  return 'red';
};

export const getPasswordValidationSchema = (passwordPolicy) => {
  let schema = Yup.string().required("New password is required");

  if (passwordPolicy.upperCase) {
    schema = schema.matches(/[A-Z]/, "Must contain at least one uppercase letter");
  }
  if (passwordPolicy.number) {
    schema = schema.matches(/[0-9]/, "Must contain at least one number");
  }
  if (passwordPolicy.specialChar) {
    schema = schema.matches(/[!@#$%^&*(),.?":{}|<>]/, "Must contain at least one special character");
  }
  if (passwordPolicy.min) {
    schema = schema.min(passwordPolicy.min, `Must be at least ${passwordPolicy.min} characters`);
  }
  if (passwordPolicy.max) {
    schema = schema.max(passwordPolicy.max, `Password cannot contain more than ${passwordPolicy.max} characters`);
  }

  return schema;
};

export const validatePasswordChecker = (password, passwordPolicy) => {
  let errors = [];

  if (passwordPolicy.upperCase && !/[A-Z]/.test(password)) {
    errors.push("one uppercase letter");
  }
  if (passwordPolicy.number && !/[0-9]/.test(password)) {
    errors.push("one number");
  }
  if (passwordPolicy.specialChar && !/[!@#$%^&*(),.?\":{}|<>]/.test(password)) {
    errors.push("one special character");
  }
  if (passwordPolicy.min && password.length < passwordPolicy.min) {
    errors.push(`at least ${passwordPolicy.min} characters`);
  }
  if (passwordPolicy.max && password.length > passwordPolicy.max) {
    errors.push(`no more than ${passwordPolicy.max} characters`);
  }

  return errors.length > 0 ? `Password must contain at least ${errors.join(", ")}.` : null;
};

export const exportFormatData = {
  [USER_TAB_TYPE]: (list) =>
    list.map((user, index) => [
      index + 1,
      user.name,
      user.email,
      user.uid,
      user.phone,
      user.designation,
      user.roleAllocations.find((e) => e.role.type !== "DEFAULT")?.role.name || user.roleAllocations[0]?.role.name,
    ]),
  [GROUP_TAB_TYPE]: (list) =>
    list.map((group, index) => [index + 1, group.name, group.role, group.groups, group.users]),
};

export const formatDataForTable = (data, navigate, handleDebounceDeleteRole) => {
  return data.map(item => {
    return ({
      cellData: [
        {
          value: (
            <>
              {item.name}{" "}
              {item.type === "DEFAULT" && (
                <span className="extracontent">Across all employee</span>
              )}
            </>
          ),
          className: "userrlescont",
        },
        { value: item.description },
        {
          value: (
            <>
              <div>
                <span onClick={() => navigate(`/roles/permission/view/${item.id}`)} className=" d-flex align-items-center gap-2 cursor-pointer">
                  <UserSimpleIcon /><span className="usericonclick">({item.assignedUsers})</span>
                </span>
              </div>
            </>
          ),
        },
        {
          value: (
            <>
              {
                <div>
                  <span
                    onClick={() => item.type === "PRIVATE" ?
                      navigate(`/roles/permission/manage/${item.id}`)
                      :
                      navigate(`/roles/permission/view/${item.id}`)
                    }
                    className="manageusers"
                  >
                    View
                  </span>

                  {item.type === "PRIVATE" && <span className="line-space"> | </span>}

                  {item.type === "PRIVATE" ?
                    item.deleteLoading ?
                      <span className="manageusers"> <span className="delete-loader"> </span> </span>
                      :
                      <span className="manageusers" onClick={() => handleDebounceDeleteRole(data, item.id)} > {item.deleteLoading ? <span className="delete-loader"></span> : "delete"} </span>
                    :
                    <></>
                  }

                </div>}
            </>
          ),
        },
      ],
    })
  });
};

export const isValidFile = (file, acceptedTypeList = []) => {
  if (!file) return false;
  if (!acceptedTypeList.includes(file.type)) return false;
  return true;
};

export const removeDuplicatesByKey = (data, key) => {
  const uniqueIds = new Set();
  return data.filter(item => {
    if (!uniqueIds.has(item[key])) {
      uniqueIds.add(item[key]);
      return true;
    }
    return false;
  });
}

export const removeMatchedDataUsingKey = (alreadySelectedGroup, uniqueGroups, key) => {
  const uniqueGroupIds = uniqueGroups.map(group => group[key]);
  return alreadySelectedGroup.filter(group => !uniqueGroupIds.includes(group[key]));
}

export const removeDuplicatesValue = (arr) => {
  return [...new Set(arr)];
}

export const removeMatchedValues = (list, removeIds) => {
  return list.filter(value => !removeIds.includes(value));
}


export const checkPermissionExist = (permission) => {
  const storeData = unoStore.getState()
  const userData = storeData.user;
  let allPermissions = userData.user.permissions;
  if (permission === BY_PASS_PERMISSION) return true;
  return allPermissions.includes(permission);
}

export const checkAllPermissionExist = (permissions) => {
  const storeData = unoStore.getState()
  const userData = storeData.user;
  let allPermissions = userData.user.permissions;
  if (permissions.includes(BY_PASS_PERMISSION)) return true;
  return permissions.every(e => allPermissions.includes(e));
}

export const checkAnyPermissionExist = (permissions) => {
  const storeData = unoStore.getState()
  const userData = storeData.user;
  let allPermissions = userData.user.permissions;
  if (permissions.includes(BY_PASS_PERMISSION)) return true;
  return permissions.some(e => allPermissions.includes(e));
}

export const visibleAssignSectionList = () => {
  const storeData = unoStore.getState();
  const { config } = storeData.config;
  return (config?.permissionTabsForResourceAssign || []);
}

export const visibleAssignSection = (metrics = []) => {
  let permissionTabsForResourceAssign = visibleAssignSectionList();
  return permissionTabsForResourceAssign.map(e => ({
    ...e,
    id_link: constantKeyMap[e.id_link],
    count: metrics[e.id_link] || 0,
  }));
}

export const searchCategory = (ID, category) => {
  if (category && category.id === ID) return category;
  if (category && category.children && category.children.length > 0) {
    for (const child of category.children) {
      const result = searchCategory(ID, child);
      if (result) return result;
    }
  }
  return null;
};

export const findCategoryOption = (category, id) => {
  if (category.id === id) {
    return category;
  }

  if (category.children && category.children.length > 0) {
    for (const child of category.children) {
      const result = findCategoryOption(child, id);
      if (result) {
        return result;
      }
    }
  }
  return null;
};

export const findAllCategoryIds = (category, ids = []) => {
  ids.push({ name: category.name, id: category.id });
  if (category.children && category.children.length > 0) {
    for (const child of category.children) {
      findAllCategoryIds(child, ids);
    }
  }
  return ids
};

export const sanitizeString = (data="") =>{
   return data.trim().toLowerCase().replace(/\s+/g, "");
}

export const sanitizeWithRemoveSpace = (data="") =>{
   return data.trim().replace(/\s+/g, "");
}